import { computed, makeObservable, observable, runInAction } from 'mobx';
import { cyrillicToLatin } from 'utils/yarolit-helper';
import { parseShape, wrapPromise } from '../utils';
import { getAgesSuffix } from 'utils/plurals-declination';
import { GET_TICKET_OPTIONS } from 'api/graphql/tickets';

export default class Specialist {
    onlineCount = 0;
    offlineCount = 0;

    constructor(model, data, { teaserLoaded = false, pageLoaded = false }) {
        this.id = data.id;
        this.model = model;
        this.data = data;
        this.openEvents = [];
        this.ticketOptions = [];
        this.teaserLoaded = teaserLoaded;
        this.pageLoaded = pageLoaded;
        this.photo = data.photoResized || data.photo;
        this.specialisation = data.specialisation;
        this.direction = data.direction;
        this.name = data.name;
        runInAction(() => {
            this.onlineCount = this.data.onlineCount;
            this.offlineCount = this.data.offlineCount;
        });

        makeObservable(this, {
            openEvents: observable,
            offlineEvents: computed,
            onlineEvents: computed,
            workingWithPairs: computed,
            workingOffline: computed,
            onlineCount: observable,
            offlineCount: observable,
            ticketOptions: observable
        })
    }

    get ticketOptionsData() {
        return wrapPromise(this.getTicketOptions());
    }

    getTicketOptions() {
        return this.model.apollo.query({
            query: GET_TICKET_OPTIONS,
            variables: {
                specialist: this.id
            }
        }).then((response) => {
            const parsed = parseShape(response)['data']['ticketsOptions'];
            runInAction(() => {
                parsed.forEach(json => this.assignTicketOptions(json))
            });
            return this.ticketOptions;
        })
        .catch(err => {
            console.error(err);
        })
    }

    get cities() {
        if (this.openEvents.length > 0) {
            return this._citiesFromEvents();
        }
        return this._citiesFromAddress();
    }

    _citiesFromAddress() {
        const cities = new Map();
        for (const address of this.data.addresses) {
            if (!address.isActive) { continue; }
            cities.set(cyrillicToLatin(address.town), address.town);
        }
        return cities;
    }

    _citiesFromEvents() {
        const cities = new Map();
        for (const event of this.offlineEvents) {
            const address = event.address;
            cities.set(cyrillicToLatin(address.town), address.town);
        }
        return cities;
    }

    get city() {
        if (this.cities.size > 0) {
            return this.cities.entries().next().value[1];
        } else {
            return null;
        }
    }

    get districts() {
        if (this.openEvents.length > 0) {
            return this._districtsFromEvents();
        }
        return this._districtsFromAddress();
    }

    _districtsFromAddress() {
        const districts = new Map();
        for (const address of this.data.addresses) {
            if (!address.isActive) { continue; }
            const { town, district } = address;
            districts.set(`${cyrillicToLatin(town)}--${cyrillicToLatin(district)}`, district);
        }
        return districts;
    }

    _districtsFromEvents() {
        const districts = new Map();
        for (const event of this.offlineEvents) {
            const address = event.address;
            const { town, district } = address;
            districts.set(`${cyrillicToLatin(town)}--${cyrillicToLatin(district)}`, district);
        }
        return districts;
    }

    get slug() {
        return this.data.slug;
    }

    get problems() {
        return this.data.problems;
    }

    get workingWithPairs() {
        return this.data.pairDetails !== 'не работает с парами'
    }

    get workingOffline() {
        return this.data.offlineCount > 0;
    }

    get offlineEvents() {
        return this.openEvents.filter(oe => oe.service.meetingType === 'OFF');
    }

    get onlineEvents() {
        return this.openEvents.filter(oe => oe.service.meetingType === 'ON');
    }

    assignOpenEvents(json) {
        const foundEvent = this.openEvents.find(to => to.id === json.id);
        if (foundEvent) { return; }
        this.openEvents.push(json);
    }

    assignTicketOptions(json) {
        const foundTicketOption = this.ticketOptions.find(to => to.id === json.id);
        if (foundTicketOption) { return; }
        this.ticketOptions.push(json)
    }

    updateFromJson(json, { teaserLoaded = false, pageLoaded = false } = {}) {
        this.data = { ...this.data, ...json}
        this.teaserLoaded = teaserLoaded;
        this.pageLoaded = pageLoaded;
        if (json.onlineCount !== undefined) {
            runInAction(() => this.onlineCount = json.onlineCount);
        }
        if (json.offlineCount !== undefined) {
            runInAction(() => this.offlineCount = json.offlineCount);
        }
    }

    getPK(date, service = 'ON') {
        const event = this.openEvents.find(oe => oe.startDate === date && oe.service.meetingType === service);
        if (!event) {
            console.error(`no event with date ${date} found for specialist ${this.name}`);
        } else {
            return event.pk;
        }
    }

    getEventByPK(pk) {
        const event = this.openEvents.find(oe => oe.pk === pk);
        if (!event) {
            console.error(`no event with pk ${pk} found for specialist ${this.name}`);
        } else {
            return event;
        }
    }

    filterEvents({meetingType, city, district}) {
        return this.openEvents.filter((event) => {
            if ((meetingType === 'ON') || (meetingType && !district && !city)) {
                return event.service.meetingType === meetingType;
            }
            if (city && !district) {
                if (event.service.meetingType !== 'OFF') {
                    return false;
                }
                const { address } = event;
                const eventCity = cyrillicToLatin(address.town);
                return event.service.meetingType === 'OFF' && event.specialist.id === this.id && eventCity === city;
            }
            if (district) {
                if (event.service.meetingType !== 'OFF') {
                    return false;
                }
                const { address } = event;
                const eventDistrict = `${cyrillicToLatin(address.town)}--${cyrillicToLatin(address.district)}`;
                return event.service.meetingType === 'OFF' && event.specialist.id === this.id && eventDistrict === district
            }
            return true;
        });
    }

    get town() {
        const activeAddresses = this.data.addresses.filter(ad => ad.isActive);
        if (activeAddresses.length > 0) {
            return activeAddresses[0].town;
        } else {
            return null;
        }
    }

    get experience() {
        const years = new Date().getFullYear() - new Date(this.data.experience).getFullYear();
        const yearsSuffix = getAgesSuffix(years);
        return `${years} ${yearsSuffix}`;
    }

    get hasMemberships() {
        return this.data?.memberships?.length > 0 && this.data.memberships.some(membership => membership.short !== '' || membership.full !== '' );
    }

    get hasEducations() {
        return this.data?.educations?.length > 0;
    }

    get hasAddress() {
        return this.data.addresses && this.data.addresses.filter(ad => ad.isActive).length > 0;
    }

    get description() {
        if (!this.data.descriptionFull) return '';
        const formatDescription = this.data.descriptionFull.split('\n- ');
        return {
            title: formatDescription[0],
            list: formatDescription.slice(1)
        }
    }

    get addresses() {
        if (!this.hasAddress) { return null; }
        return this.data.addresses.filter(ad => ad.isActive);
    }

    get educations() {
        if (!this.hasEducations) { return null; }
        return this.data.educations;
    }

    get memberships() {
        if (!this.hasMemberships) { return null; }
        return this.data.memberships;
    }

    get serviceOptions() {
        const serviceOptions = [];
        if (this.offlineCount > 0) {
            serviceOptions.push(true)
        }
        if (this.onlineCount > 0) {
            serviceOptions.push(false);
        }
        return serviceOptions;
    }

    get closestEvent() {
        return this.data.closestEvent;
    }

}
